import React, { FC, useMemo } from "react";
import { Box, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useIntl } from "react-intl";

import { STATION_TRAIN_ANALYTICS_ROWS_LIMIT } from "@framework/types";

import { STATION_TILE_ANALYTICS_LABEL_MESSAGE_IDS } from "../../../../../../../station-tile-analytics-meta";
import { IStationTrainAnalyticsRow } from "../interfaces";

export interface IStationTrainAnalyticsTableProps {
  rowIds: string[];
  rows: IStationTrainAnalyticsRow[];
}

const LABEL_SX = {
  fontSize: 13,
  lineHeight: "16px",
  fontWeight: 700,
  color: "#121113",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
} as const;

const VALUE_SX = {
  fontSize: 13,
  lineHeight: "16px",
  fontWeight: 700,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const normalizePartText = (text: string): string =>
  text
    .replace(/^\s*\(/, " ")
    .replace(/\)\s*$/, "")
    .trim();

const isNumericPart = (text: string): boolean => /^[\d.,+\-—]+$/.test(text.trim());

export const buildStationTrainAnalyticsDisplayRows = (
  rowIds: string[],
  rows: IStationTrainAnalyticsRow[],
): Array<IStationTrainAnalyticsRow | null> => {
  const rowsById = Object.fromEntries(rows.map(row => [row.rowId, row]));
  const selectedRows = rowIds
    .slice(0, STATION_TRAIN_ANALYTICS_ROWS_LIMIT)
    .map(rowId => rowsById[rowId] ?? { rowId, parts: [{ text: "—" }] });
  const emptyRows = Array.from(
    { length: Math.max(0, STATION_TRAIN_ANALYTICS_ROWS_LIMIT - selectedRows.length) },
    () => null,
  );

  return [...selectedRows, ...emptyRows];
};

export const StationTrainAnalyticsTable: FC<IStationTrainAnalyticsTableProps> = ({ rowIds, rows }) => {
  const { formatMessage } = useIntl();

  const displayRows = useMemo(() => buildStationTrainAnalyticsDisplayRows(rowIds, rows), [rowIds, rows]);

  return (
    <Box sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
      <Table size="small" sx={{ tableLayout: "fixed", width: "100%", borderCollapse: "collapse" }}>
        <TableBody>
          {displayRows.map((row, index) => (
            <TableRow
              key={row?.rowId ?? `empty-${index}`}
              aria-hidden={row ? undefined : true}
              sx={{
                "& td": {
                  border: 0,
                  borderBottom: index < displayRows.length - 1 ? "1px solid #DADDE5" : "none",
                  lineHeight: 0,
                  py: 0.25,
                  px: 0,
                  verticalAlign: "middle",
                },
              }}
            >
              <TableCell sx={{ width: "52%", overflow: "hidden", pr: 1 }}>
                {row ? (
                  <Typography
                    sx={LABEL_SX}
                    title={formatMessage({ id: STATION_TILE_ANALYTICS_LABEL_MESSAGE_IDS[row.rowId] })}
                  >
                    {formatMessage({ id: STATION_TILE_ANALYTICS_LABEL_MESSAGE_IDS[row.rowId] })}:
                  </Typography>
                ) : (
                  <Box aria-hidden sx={{ height: "16px" }} />
                )}
              </TableCell>
              <TableCell sx={{ width: "48%", overflow: "hidden", textAlign: "right" }}>
                {row ? (
                  <Typography component="span" sx={VALUE_SX}>
                    {row.parts.map((part, partIndex) => {
                      const text = normalizePartText(part.text);
                      const color = part.color ?? (text === "—" ? "#8A93A8" : "#121113");

                      return (
                        <Box
                          key={`${row.rowId}-${partIndex}`}
                          component="span"
                          sx={{
                            color,
                            fontWeight: isNumericPart(text) || text === "—" ? 700 : 600,
                            ...(partIndex > 0 ? { ml: text.startsWith("/") ? 0 : 0.25 } : {}),
                          }}
                        >
                          {text}
                        </Box>
                      );
                    })}
                  </Typography>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
